import React from 'react';
import useLogPage from '../useLogPage';
import LogFilters from '../LogFilters';
import LogTable from '../LogTable';
import LogStatCards from '../LogStatCards';
import { TrendChart, GroupedBarChart } from '../LogCharts';
import { LogService } from '../../../services/activityLogService';

const ACTION_OPTIONS = [
  { value: 'ORDER_DEDUCTION', label: 'Order Deduction' },
  { value: 'STOCK_IN',        label: 'Stock In'        },
  { value: 'STOCK_OUT',       label: 'Stock Out'       },
  { value: 'ADJUSTMENT',      label: 'Adjustment'      },
  { value: 'TRANSFER',        label: 'Transfer'        },
];

const actionBadge = (v) => {
  const map = {
    order_deduction: ['#E0F2FE','#0369A1'],
    stock_in:        ['#D1FAE5','#065F46'],
    stock_out:       ['#FEE2E2','#991B1B'],
    adjustment:      ['#FEF3C7','#92400E'],
    transfer:        ['#DBEAFE','#1E40AF'],
  };
  const [bg, color] = map[v?.toLowerCase()] || ['#F3F4F6','#374151'];
  return <span className="al-badge" style={{ background: bg, color }}>{v || '—'}</span>;
};

const COLUMNS = [
  { key: 'id',            label: '#',          render: (v) => <span className="al-mono">{v}</span> },
  { key: 'product_id',    label: 'Variant ID', render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'product_name',  label: 'Product',    render: (v) => v || '—' },
  { key: 'sku',           label: 'SKU',        render: (v) => <span className="al-mono">{v || '—'}</span> },
  { key: 'action',        label: 'Action',     render: actionBadge },
  { key: 'quantity',      label: 'Qty',        render: (v) => v ?? '—' },
  { key: 'before_qty',    label: 'Before' },
  { key: 'after_qty',     label: 'After' },
  { key: 'business_name', label: 'Business',   render: (v) => v || '—' },
  { key: 'created_at',    label: 'Time',       render: (v) => v ? new Date(v).toLocaleString('en-IN') : '—' },
];

const InventoryLog = () => {
  const lp = useLogPage(LogService.inventory);
  const s = lp.stats || {};

  const handleExport = () => {
    const { search, status, dateFrom, dateTo } = lp.filters;
    LogService.exportInventory({
      ...(search   ? { search }              : {}),
      ...(status   ? { status }              : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo   ? { date_to:   dateTo   } : {}),
      format: 'excel',
    });
  };

  const stats = [
    { label: 'Total Events',     value: lp.totalItems.toLocaleString(),                                                              color: '#84CC16' },
    { label: 'Order Deductions', value: (s.breakdown?.ORDER_DEDUCTION ?? s.breakdown?.order_deduction ?? 0).toLocaleString(),        color: '#06B6D4' },
    { label: 'Stock In',         value: (s.breakdown?.STOCK_IN        ?? s.breakdown?.stock_in        ?? 0).toLocaleString(),        color: '#10B981' },
    { label: 'Stock Out',        value: (s.breakdown?.STOCK_OUT       ?? s.breakdown?.stock_out       ?? 0).toLocaleString(),        color: '#EF4444' },
  ];

  const barData = lp.donut.length
    ? lp.donut
    : Object.entries(s.breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="al-page">
      <LogStatCards stats={stats} />

      <div className="al-charts">
        <TrendChart title="Inventory Events Over Time" data={lp.trend} color="#84CC16" />
        <GroupedBarChart
          title="Events by Action"
          data={barData}
          bars={[{ key: 'value', label: 'Count' }]}
          xKey="name"
        />
      </div>

      <LogFilters
        filters={lp.filters}
        onFilterChange={lp.handleFilterChange}
        onApply={lp.handleApply}
        onReset={lp.handleReset}
        statusOptions={ACTION_OPTIONS}
        loading={lp.loading}
      />

      <LogTable
        title="Inventory Logs"
        columns={COLUMNS}
        rows={lp.rows}
        loading={lp.loading}
        error={lp.error}
        page={lp.page}
        totalPages={lp.totalPages}
        totalItems={lp.totalItems}
        pageSize={lp.filters.pageSize}
        onPageChange={lp.handlePageChange}
        onExport={handleExport}
        onRefresh={lp.handleRefresh}
      />
    </div>
  );
};

export default InventoryLog;
